import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { useTeamMembers } from '../hooks/useSupabase';
import * as FiIcons from 'react-icons/fi';

const { FiArrowRight, FiAward, FiFlag, FiShield, FiStar, FiTarget, FiTrendingUp, FiUserPlus, FiUsers } = FiIcons;

const STAGES = [
  { key: 'K6', label: 'Κ6', title: 'Πρώτα βήματα', description: 'Γνωριμία με την μπάλα, κίνηση, παιχνίδι και αγάπη για το ποδόσφαιρο.', icon: FiStar },
  { key: 'K10', label: 'Κ10', title: 'Βάσεις παιχνιδιού', description: 'Τεχνική με την μπάλα, συνεργασία, αντίληψη χώρου και βασικές αρχές παιχνιδιού.', icon: FiTarget },
  { key: 'K12', label: 'Κ12', title: 'Τεχνική εξέλιξη', description: 'Πιο σύνθετες αποφάσεις, ταχύτητα εκτέλεσης, ομαδικότητα και σταδιακή αγωνιστική ανάπτυξη.', icon: FiTrendingUp },
  { key: 'K14', label: 'Κ14', title: 'Προαγωνιστικό στάδιο', description: 'Προετοιμασία για μεγαλύτερες αγωνιστικές απαιτήσεις και μετάβαση προς την Κ17.', icon: FiFlag },
  { key: 'K17', label: 'Κ17', title: 'Πανελλήνιο επίπεδο', description: 'Αγωνιστική ομάδα νεανίδων με στόχο την εξέλιξη, την εμπειρία πρωταθλήματος και τη σύνδεση με την Α’ Ομάδα.', icon: FiShield },
  { key: 'Senior', label: 'Α’ Ομάδα', title: 'Γυναικεία ομάδα', description: 'Ο τελικός σταθμός της αναπτυξιακής διαδρομής του συλλόγου και το επόμενο επίπεδο για τις αθλήτριες που προχωρούν.', icon: FiAward },
];

const normalizeCategory = (value = '') => String(value).trim().toUpperCase();

const AcademyPathway = () => {
  const { data: players, loading } = useTeamMembers(false);

  const counts = useMemo(() => {
    const map = {};
    (players || []).forEach((player) => {
      (player.team_categories || []).forEach((category) => {
        const normalized = normalizeCategory(category);
        map[normalized] = (map[normalized] || 0) + 1;
      });
    });
    return map;
  }, [players]);

  const getCount = (stageKey) => {
    if (stageKey === 'Senior') return counts.SENIOR || 0;
    return counts[stageKey] || 0;
  };

  return (
    <section className="relative overflow-hidden bg-slate-950 py-16 text-white md:py-20" data-academy-pathway="true">
      <div className="absolute inset-0 opacity-40" aria-hidden="true" style={{ backgroundImage: 'radial-gradient(circle at 15% 20%, rgba(37,99,235,.38), transparent 28%), radial-gradient(circle at 85% 80%, rgba(14,165,233,.24), transparent 28%)' }} />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <div className="mx-auto mb-5 inline-flex items-center gap-2 rounded-full border border-blue-300/20 bg-blue-500/10 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-blue-100">
            <SafeIcon icon={FiUsers} className="h-4 w-4" /> AOX Academy Pathway
          </div>
          <h2 className="text-3xl font-black md:text-5xl">Από την Ακαδημία στην Α’ Ομάδα</h2>
          <p className="mt-5 text-base leading-relaxed text-slate-300 md:text-lg">
            Μία ξεκάθαρη αναπτυξιακή διαδρομή ώστε κάθε κορίτσι να γνωρίζει ποιο είναι το επόμενο βήμα της μέσα στον Α.Ο. Χανιά.
          </p>
        </div>

        <div className="mt-12 grid gap-4 lg:grid-cols-6">
          {STAGES.map((stage, index) => {
            const count = getCount(stage.key);
            return (
              <motion.div
                key={stage.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.25 }}
                transition={{ duration: 0.35, delay: index * 0.05 }}
                className="relative rounded-2xl border border-white/10 bg-white/[0.06] p-5 shadow-xl backdrop-blur-sm"
              >
                {index < STAGES.length - 1 && (
                  <div className="absolute -right-3 top-1/2 z-10 hidden -translate-y-1/2 lg:block">
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-500 shadow-lg">
                      <SafeIcon icon={FiArrowRight} className="h-3.5 w-3.5" />
                    </div>
                  </div>
                )}

                <div className="flex items-start justify-between gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-500/20 text-blue-200">
                    <SafeIcon icon={stage.icon} className="h-5 w-5" />
                  </div>
                  <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-black tracking-wide text-blue-100">{stage.label}</span>
                </div>

                <h3 className="mt-5 text-lg font-black">{stage.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-300">{stage.description}</p>

                {!loading && count > 0 && (
                  <div className="mt-4 border-t border-white/10 pt-3 text-xs font-bold text-emerald-200">
                    {count} {count === 1 ? 'δημοσιευμένη αθλήτρια' : 'δημοσιευμένες αθλήτριες'}
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>

        <div className="mt-10 rounded-2xl border border-white/10 bg-white/[0.06] p-6 md:flex md:items-center md:justify-between md:gap-8">
          <div>
            <div className="text-sm font-black uppercase tracking-[0.16em] text-blue-200">Το πρώτο βήμα</div>
            <h3 className="mt-2 text-2xl font-black">Η διαδρομή ξεκινά με μία προπόνηση.</h3>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-300">Η ένταξη σε κάθε ηλικιακό στάδιο γίνεται με βάση την ηλικία, την εμπειρία και την αξιολόγηση των προπονητών. Η εξέλιξη δεν είναι αγώνας ταχύτητας — είναι συνεχής πρόοδος.</p>
          </div>
          <div className="mt-5 flex flex-col gap-3 sm:flex-row md:mt-0 md:flex-col lg:flex-row">
            <Link to="/academy-registration" className="inline-flex items-center justify-center rounded-xl bg-white px-5 py-3 font-black text-primary-800 transition-colors hover:bg-blue-50">
              <SafeIcon icon={FiUserPlus} className="mr-2 h-4 w-4" /> Εγγραφή
            </Link>
            <Link to="/matches" className="inline-flex items-center justify-center rounded-xl border border-white/20 bg-white/10 px-5 py-3 font-black text-white transition-colors hover:bg-white/15">
              Αγώνες Κ17 / Senior <SafeIcon icon={FiArrowRight} className="ml-2 h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AcademyPathway;
